import { useState } from "react";
import { Play, ChevronDown, ArrowRight } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";

interface Lesson { number: string; title: string; }
interface Module { number: number; title: string; lessons: Lesson[]; }

const modules: Module[] = [
  { number: 1, title: "The Foundation: Know Your Audience, Know Your Money", lessons: [{ number: "1.1", title: "Why Most Creators Stay Broke (And How You Won't)" }, { number: "1.2", title: "Finding Your First Winning Keyword" }, { number: "1.3", title: "Turning Keywords Into Content Ideas" }, { number: "1.4", title: "Building Your Content Calendar" }] },
  { number: 2, title: "The Script Machine: Creating Content That Converts", lessons: [{ number: "2.1", title: "The Anatomy of a Viral Script" }, { number: "2.2", title: "Generating Scripts with Nexis AI" }, { number: "2.3", title: "Editing and Personalising AI Scripts" }, { number: "2.4", title: "Batching Content Like a Pro" }] },
  { number: 3, title: "The Product Engine: Building Your First Digital Product", lessons: [{ number: "3.1", title: "What to Sell (And What Not to Sell)" }, { number: "3.2", title: "Creating Your Product with Monetisation AI" }, { number: "3.3", title: "Packaging and Pricing for Maximum Sales" }, { number: "3.4", title: "Writing a Sales Page That Converts" }, { number: "3.5", title: "Setting Up Your Payment System" }] },
  { number: 4, title: "The Sales System: Turning Viewers Into Buyers", lessons: [{ number: "4.1", title: "Why Email Beats Every Other Channel" }, { number: "4.2", title: "Building Your Email List from Scratch" }, { number: "4.3", title: "The Welcome Sequence That Sells" }, { number: "4.4", title: "Writing Emails People Actually Open" }, { number: "4.5", title: "Automating Your Sales Funnel" }] },
  { number: 5, title: "The Launch & Scale Blueprint", lessons: [{ number: "5.1", title: "Planning Your Launch Week" }, { number: "5.2", title: "Repurposing Content with Clip AI" }, { number: "5.3", title: "Scaling What Works" }, { number: "5.4", title: "Your 90-Day Growth Roadmap" }] },
];

export const BonusCourseSection = () => {
  const [showCurriculum, setShowCurriculum] = useState(false);
  const [openModule, setOpenModule] = useState<number>(0);

  return (
    <section className="py-12 md:py-20 relative z-10" style={{ background: "#FAFAFA" }}>
      <div className="container mx-auto px-4 md:px-6">
        <ScrollReveal>
          <div className="text-center mb-10">
            <p className="text-sm font-semibold mb-4" style={{ color: "#7C3AED", letterSpacing: "2px", fontSize: "14px" }}>
              INCLUDED WITH EVERY PLAN
            </p>
            <h2 className="font-display font-bold mb-4 leading-tight" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 36px)" }}>
              BONUS: Free Video Training Included With Every Plan
            </h2>
            <p className="mx-auto mb-4 leading-relaxed" style={{ color: "#4B5563", fontSize: "16px", maxWidth: "600px" }}>
              22 video lessons walking you through every tool — from finding your niche to launching your first product. Value: <span className="line-through" style={{ color: "#9CA3AF" }}>$497</span> — yours <span className="font-bold" style={{ color: "#F59E0B" }}>FREE</span>.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="max-w-[600px] mx-auto">
            <button
              onClick={() => setShowCurriculum(!showCurriculum)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all hover:bg-purple-50"
              style={{ color: "#7C3AED", border: "1px solid rgba(124,58,237,0.3)" }}
            >
              {showCurriculum ? "Hide curriculum" : "View curriculum"}
              <ChevronDown className={`w-4 h-4 transition-transform ${showCurriculum ? "rotate-180" : ""}`} />
            </button>

            {showCurriculum && (
              <div className="mt-4 space-y-2">
                {modules.map((mod) => {
                  const isOpen = openModule === mod.number;
                  return (
                    <div key={mod.number}>
                      <button
                        onClick={() => setOpenModule(isOpen ? 0 : mod.number)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-left min-h-[48px] bg-white"
                        style={{ borderLeft: isOpen ? "3px solid #7C3AED" : "3px solid transparent", border: "1px solid #E5E7EB" }}
                      >
                        <span className="font-semibold text-sm truncate" style={{ color: "#111827" }}>Module {mod.number} — {mod.title}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline-block" style={{ background: "rgba(124,58,237,0.08)", color: "#7C3AED" }}>
                            {mod.lessons.length} Lessons
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "#6B7280" }} />
                        </div>
                      </button>
                      {isOpen && (
                        <div className="rounded-b-lg overflow-hidden" style={{ border: "1px solid #E5E7EB", borderTop: "none" }}>
                          {mod.lessons.map((lesson, li) => (
                            <div
                              key={lesson.number}
                              className="flex items-center justify-between px-4 py-3 bg-white"
                              style={{ borderBottom: li < mod.lessons.length - 1 ? "1px solid #E5E7EB" : "none" }}
                            >
                              <div className="flex items-center gap-3">
                                <Play className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#7C3AED" }} />
                                <span className="text-sm" style={{ color: "#4B5563" }}>{lesson.title}</span>
                              </div>
                              <span className="text-xs flex-shrink-0 ml-4" style={{ color: "#9CA3AF" }}>8–12 min</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="text-center mt-6">
              <p className="text-xs" style={{ color: "#9CA3AF" }}>
                22 video lessons • 5 modules • 4.5+ hours of content • Homework included • Lifetime access
              </p>
              <a href="#pricing" className="inline-flex items-center gap-1 mt-3 text-sm font-medium transition-colors hover:underline" style={{ color: "#7C3AED" }}>
                Included free with all plans <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};
